import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface HelpItem {
  id: number;
  title: string;
  parentId: number | null;
  layout: string | null;
  description: string | null;
  lang: string;
}

interface SectionMeta {
  name: string;
  icon: string;
  bg: string;
  desc: string;
}

const ROUTE_MAP: Record<string, string> = {
  '/dashboard':  'dashboard',
  '/timesheets': 'timesheets',
  '/approve':    'timesheets',
  '/reports':    'timesheets',
  '/hr':         'hr',
  '/toil':       'hr',
  '/docs':       'documents',
  '/crm':        'crm',
  '/community':  'community',
  '/equipment':  'equipment',
  '/training':   'training',
  '/booking':    'booking',
  '/finance':    'finance',
  
};

const GROUPS = [
  { label: 'Main',       keys: ['dashboard', 'timesheets', 'hr', 'documents', 'crm'] },
  { label: 'Operations', keys: ['community', 'equipment', 'training', 'booking', 'finance'] },
  { label: 'Account',    keys: ['settings'] },
];

function parseMeta(raw: string | null): SectionMeta {
  try {
    if (raw) {
      const obj = JSON.parse(raw);
      if (typeof obj === 'object') return obj as SectionMeta;
    }
  } catch {}
  return { name: raw || '', icon: '📄', bg: 'rgba(100,116,139,.12)', desc: '' };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function Help({ open, onClose }: Props) {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [items, setItems] = useState<HelpItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const lang = i18n.language?.slice(0, 2) || 'en';
      const { data } = await axios.get(`helpdesk?lang=${lang}`);
      setItems(Array.isArray(data) ? data : []);
    } catch {}
  }, [i18n.language]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const sections = items.filter(it => it.parentId === null);

  // Auto-select section by current route
  useEffect(() => {
    if (!open || sections.length === 0) return;
    const routeKey = ROUTE_MAP[location.pathname] ?? 'dashboard';
    const matched = sections.find(s => s.title === routeKey);
    if (matched) {
      setSelectedId(matched.id);
    } else if (selectedId === null) {
      setSelectedId(sections[0]?.id ?? null);
    }
  }, [open, sections.length, location.pathname]); // eslint-disable-line

  // При смене раздела всегда открываем его первую главу
  useEffect(() => { setSelectedTopicId(null); }, [selectedId]);

  const q = search.trim().toLowerCase();
  // Поиск идёт и по названию раздела, и по его главам (заголовок + текст)
  const sectionMatches = (s: HelpItem) => {
    if (!q) return true;
    if (parseMeta(s.description).name.toLowerCase().includes(q)) return true;
    return items.some(
      it => it.parentId === s.id && `${it.title} ${it.layout ?? ''}`.toLowerCase().includes(q),
    );
  };

  const filteredGroups = GROUPS.map(g => ({
    ...g,
    sections: sections.filter(s => g.keys.includes(s.title) && sectionMatches(s)),
  })).filter(g => g.sections.length > 0);

  const selectedSection = sections.find(s => s.id === selectedId) ?? null;
  const selectedMeta   = selectedSection ? parseMeta(selectedSection.description) : null;
  const topics         = items.filter(it => it.parentId === selectedId);
  const visibleTopics  = q
    ? topics.filter(t => `${t.title} ${t.layout ?? ''}`.toLowerCase().includes(q))
    : topics;
  const selectedTopic  = visibleTopics.find(t => t.id === selectedTopicId) ?? visibleTopics[0] ?? null;

  // Раньше все главы склеивались в одну длинную страницу:
  // const htmlContent = topics.map(t => t.layout || '').join('\n<hr/>\n');
  // Теперь глава выбирается в сайдбаре — длинные руководства читаются удобнее.

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const panel = (
    <>
      {/* Overlay */}
      <div className={`help-overlay${open ? ' open' : ''}`} onClick={onClose} />

      {/* Panel */}
      <div className={`help-panel${open ? ' open' : ''}`} role="dialog" aria-modal="true">

        {/* Header */}
        <div className="help-ph">
          <div className="help-ph-left">
            <div className="help-ph-icon">?</div>
            <div>
              <div className="help-ph-title">Help Center</div>
              <div className="help-ph-sub">Guides and documentation</div>
            </div>
          </div>
          <button type="button" className="help-ph-close" onClick={onClose} title="Close">✕</button>
        </div>

        {/* Body */}
        <div className="help-body">

          {/* Left sidebar */}
          <div className="help-sidebar">
            <div className="help-search-wrap">
              <span className="help-search-ic">🔍</span>
              <input
                className="help-search"
                placeholder="Search..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {filteredGroups.map(group => (
              <React.Fragment key={group.label}>
                <div className="help-sidebar-lbl">{group.label}</div>
                {group.sections.map(sec => {
                  const meta = parseMeta(sec.description);
                  return (
                    <React.Fragment key={sec.id}>
                      <button
                        className={`help-sec-item${selectedId === sec.id ? ' active' : ''}`}
                        onClick={() => setSelectedId(sec.id)}
                        type="button"
                      >
                        <div className="help-sec-ic" style={{ background: meta.bg }}>{meta.icon}</div>
                        <span className="help-sec-name">{meta.name}</span>
                      </button>

                      {/* Главы активного раздела */}
                      {selectedId === sec.id && visibleTopics.length > 1 && (
                        <div className="help-topic-list">
                          {visibleTopics.map(tp => (
                            <button
                              key={tp.id}
                              type="button"
                              className={`help-topic-item${selectedTopic?.id === tp.id ? ' active' : ''}`}
                              onClick={() => setSelectedTopicId(tp.id)}
                            >
                              {tp.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {/* Right content */}
          <div className="help-content-area">
            {selectedMeta && (
              <div className="help-ch">
                <div className="help-ch-icon" style={{ background: selectedMeta.bg }}>
                  {selectedMeta.icon}
                </div>
                <div>
                  <div className="help-ch-title">{selectedMeta.name}</div>
                  {selectedMeta.desc && (
                    <div className="help-ch-desc">{selectedMeta.desc}</div>
                  )}
                </div>
              </div>
            )}

            {selectedTopic ? (
              <>
                {visibleTopics.length > 1 && <p className="help-topic-h">{selectedTopic.title}</p>}
                <div className="help-html" dangerouslySetInnerHTML={{ __html: selectedTopic.layout || '' }} />
              </>
            ) : selectedSection && (
              <p style={{ fontSize: 13, color: 'var(--text-color-calendar-week)', marginTop: 8 }}>
                {topics.length > 0 ? 'Nothing matches your search.' : 'No content yet for this section.'}
              </p>
            )}
          </div>

        </div>

        {/* Footer */}
        <div className="help-footer">
          <span className="help-footer-l">Can't find what you need?</span>
          <a className="help-footer-link" href="mailto:support@timeberlo.com">
            Contact Support ↗
          </a>
        </div>

      </div>
    </>
  );

  return createPortal(panel, document.body);
}
